import { useState, useEffect } from 'react';
import {
  X,
  FileText,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  MessageSquare,
  AlertTriangle,
  Eye,
  EyeOff,
  ArrowRight,
  BarChart3,
  Database,
  Download,
} from 'lucide-react';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db, appId } from '@/lib/firebase';
import { ReportCategory } from '@/types/reports';

interface ReportData {
  id: string;
  politician?: string;
  location: string;
  description?: string;
  imageUrl?: string;
  authorName?: string;
  authorPhoto?: string | null;
  createdAt?: any;
  status?: 'pending' | 'approved' | 'rejected';
  isPublic?: boolean;
  // Phase 2: Report category
  reportCategory?: ReportCategory;
  // DILG Anti-Epal Drive fields (for ANTI_EPAL category)
  isGovernmentFunded?: boolean;
  projectType?: 'government_project' | 'government_program' | 'government_activity' | 'government_property';
  projectName?: string;
  fundingSource?: 'national' | 'local' | 'both';
  violationType?: 'name' | 'image' | 'likeness' | 'combination';
  // Additional fields for other categories
  expectedTimeline?: string;
  serviceType?: string;
  complianceType?: string;
  communitySignals?: {
    validVotes: number;
    abuseFlags: number;
    commentsCount: number;
  };
}

interface AdminData {
  id: string;
  role: 'root' | 'admin';
  email: string;
  displayName: string;
  photoUrl: string | null;
  isActive: boolean;
  createdAt: any;
}

interface AdminDashboardProps {
  currentUserId: string;
  currentUserRole: 'root' | 'admin';
  onClose?: () => void; // Optional for page mode
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
  confirmAction?: (title: string, message: string, action: () => void) => void;
}

export const AdminDashboard = ({
  currentUserRole,
  onClose,
  showNotify,
  confirmAction,
}: AdminDashboardProps) => {
  const [reports, setReports] = useState<ReportData[]>([]);
  const [admins, setAdmins] = useState<AdminData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<ReportCategory | 'all'>('all');

  // Fetch all reports
  useEffect(() => {
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'reports'),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedReports = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as ReportData[];
      setReports(fetchedReports);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Fetch admins (for root admin only)
  useEffect(() => {
    if (currentUserRole !== 'root') return;
    const q = query(
      collection(db, 'artifacts', appId, 'admins'),
      orderBy('createdAt', 'asc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedAdmins = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as AdminData[];
      setAdmins(fetchedAdmins);
    });
    return () => unsubscribe();
  }, [currentUserRole]);


  // Phase 2: Filter reports by category
  const filteredReports = selectedCategory === 'all'
    ? reports
    : reports.filter((r) => {
        const reportCat = r.reportCategory || 'ANTI_EPAL';
        return reportCat === selectedCategory;
      });

  // Calculate statistics (on filtered reports)
  const stats = {
    totalReports: filteredReports.length,
    pendingReports: filteredReports.filter((r) => (r.status || 'pending') === 'pending').length,
    approvedReports: filteredReports.filter((r) => r.status === 'approved').length,
    rejectedReports: filteredReports.filter((r) => r.status === 'rejected').length,
    publicReports: filteredReports.filter((r) => r.isPublic === true).length,
    totalAdmins: admins.length,
    activeAdmins: admins.filter((a) => a.isActive).length,
    totalValidVotes: filteredReports.reduce((sum, r) => sum + (r.communitySignals?.validVotes || 0), 0),
    totalAbuseFlags: filteredReports.reduce((sum, r) => sum + (r.communitySignals?.abuseFlags || 0), 0),
    totalComments: filteredReports.reduce((sum, r) => sum + (r.communitySignals?.commentsCount || 0), 0),
  };

  // Get recent reports (last 5)
  const recentReports = filteredReports.slice(0, 5);

  // Get reports needing attention (pending with high abuse flags or low valid votes)
  const needsAttention = filteredReports.filter((r) => {
    const status = r.status || 'pending';
    const signals = r.communitySignals || { validVotes: 0, abuseFlags: 0 };
    return (
      status === 'pending' &&
      (signals.abuseFlags > signals.validVotes || signals.abuseFlags >= 3)
    );
  }).slice(0, 5);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Calculate DILG-specific analytics (only for ANTI_EPAL category, using filtered reports)
  const dilgAnalytics = {
    byProjectType: {
      government_project: filteredReports.filter((r) => r.projectType === 'government_project' && r.status === 'approved').length,
      government_program: filteredReports.filter((r) => r.projectType === 'government_program' && r.status === 'approved').length,
      government_activity: filteredReports.filter((r) => r.projectType === 'government_activity' && r.status === 'approved').length,
      government_property: filteredReports.filter((r) => r.projectType === 'government_property' && r.status === 'approved').length,
    },
    byFundingSource: {
      national: filteredReports.filter((r) => r.fundingSource === 'national' && r.status === 'approved').length,
      local: filteredReports.filter((r) => r.fundingSource === 'local' && r.status === 'approved').length,
      both: filteredReports.filter((r) => r.fundingSource === 'both' && r.status === 'approved').length,
    },
    byViolationType: {
      name: filteredReports.filter((r) => r.violationType === 'name' && r.status === 'approved').length,
      image: filteredReports.filter((r) => r.violationType === 'image' && r.status === 'approved').length,
      likeness: filteredReports.filter((r) => r.violationType === 'likeness' && r.status === 'approved').length,
      combination: filteredReports.filter((r) => r.violationType === 'combination' && r.status === 'approved').length,
    },
    governmentFundedCount: filteredReports.filter((r) => r.isGovernmentFunded && r.status === 'approved').length,
  };

  // Generate seed reports for testing
  const generateSeedReports = async () => {
    console.log('[AdminDashboard] generateSeedReports called', { hasConfirmAction: !!confirmAction, hasShowNotify: !!showNotify });
    
    // Fallback confirmation if confirmAction is not available
    const confirmMessage = 'This will create 10 sample reports with various DILG violation types for testing. Continue?';
    
    const handleConfirm = async () => {
      console.log('[AdminDashboard] User confirmed, executing seed report generation...');
      await executeSeedReportGeneration();
    };
    
    if (confirmAction && showNotify) {
      console.log('[AdminDashboard] Using confirmAction for confirmation');
      confirmAction(
        'Generate Seed Reports?',
        confirmMessage,
        handleConfirm
      );
    } else if (window.confirm(`Generate Seed Reports?\n\n${confirmMessage}`)) {
      // Fallback to window.confirm if confirmAction is not available
      console.log('[AdminDashboard] Using window.confirm for confirmation');
      await handleConfirm();
    } else {
      console.log('[AdminDashboard] User cancelled seed report generation');
    }
  };

  const executeSeedReportGeneration = async () => {
    console.log('[AdminDashboard] executeSeedReportGeneration started');
    
    // Create a fallback notification function if showNotify is not available
    const notify = showNotify || ((message: string, type?: 'success' | 'error' | 'info') => {
      console.log(`[AdminDashboard] ${type?.toUpperCase() || 'INFO'}: ${message}`);
      alert(message); // Fallback to alert if showNotify is not available
    });

    try {
      notify('Generating seed reports...', 'info');
      
      const seedData = [
        {
          politician: 'Mayor Roberto Santos',
          location: 'Barangay Health Center, Quezon City',
          description: 'Large tarpaulin with mayor\'s name and photo prominently displayed on the newly constructed health center entrance.',
          projectType: 'government_project' as const,
          projectName: 'Barangay Health Center Construction',
          fundingSource: 'local' as const,
          violationType: 'combination' as const,
          status: 'approved' as const,
          isPublic: true,
          validVotes: 15,
          abuseFlags: 2,
          commentsCount: 8,
        },
        {
          politician: 'Governor Maria Cruz',
          location: 'Provincial Highway, Laguna',
          description: 'Official\'s name painted on concrete barriers along the highway project. Clear violation of DILG policy.',
          projectType: 'government_project' as const,
          projectName: 'Provincial Highway Expansion',
          fundingSource: 'both' as const,
          violationType: 'name' as const,
          status: 'approved' as const,
          isPublic: true,
          validVotes: 22,
          abuseFlags: 1,
          commentsCount: 12,
        },
        {
          politician: 'Congressman Juan Dela Cruz',
          location: 'Public School Building, Manila',
          description: 'Congressional photo and name on school building facade. Funded by national government.',
          projectType: 'government_property' as const,
          projectName: 'Public School Building Renovation',
          fundingSource: 'national' as const,
          violationType: 'image' as const,
          status: 'approved' as const,
          isPublic: true,
          validVotes: 18,
          abuseFlags: 0,
          commentsCount: 6,
        },
        {
          politician: 'Mayor Ana Garcia',
          location: 'Public Market, Makati',
          description: 'Mayor\'s name and image on market entrance sign. Local government funded project.',
          projectType: 'government_property' as const,
          projectName: 'Public Market Renovation',
          fundingSource: 'local' as const,
          violationType: 'combination' as const,
          status: 'pending' as const,
          isPublic: false,
          validVotes: 0,
          abuseFlags: 0,
          commentsCount: 0,
        },
        {
          politician: 'Vice Mayor Carlos Reyes',
          location: 'Barangay Hall, Pasig',
          description: 'Vice mayor\'s name engraved on building cornerstone. Part of local infrastructure program.',
          projectType: 'government_project' as const,
          projectName: 'Barangay Hall Construction',
          fundingSource: 'local' as const,
          violationType: 'name' as const,
          status: 'approved' as const,
          isPublic: true,
          validVotes: 10,
          abuseFlags: 3,
          commentsCount: 5,
        },
        {
          politician: 'Senator Pedro Martinez',
          location: 'Community Center, Cebu',
          description: 'Senator\'s photo and name on community center banner. National government program.',
          projectType: 'government_program' as const,
          projectName: 'Community Development Program',
          fundingSource: 'national' as const,
          violationType: 'image' as const,
          status: 'approved' as const,
          isPublic: true,
          validVotes: 25,
          abuseFlags: 1,
          commentsCount: 15,
        },
        {
          politician: 'Mayor Liza Fernandez',
          location: 'Public Library, Davao',
          description: 'Mayor\'s name on library building. Local government project.',
          projectType: 'government_property' as const,
          projectName: 'Public Library Construction',
          fundingSource: 'local' as const,
          violationType: 'name' as const,
          status: 'pending' as const,
          isPublic: false,
          validVotes: 0,
          abuseFlags: 0,
          commentsCount: 0,
        },
        {
          politician: 'Governor Jose Torres',
          location: 'Provincial Hospital, Pampanga',
          description: 'Governor\'s image on hospital signage. Combined national and local funding.',
          projectType: 'government_project' as const,
          projectName: 'Provincial Hospital Expansion',
          fundingSource: 'both' as const,
          violationType: 'likeness' as const,
          status: 'approved' as const,
          isPublic: true,
          validVotes: 20,
          abuseFlags: 2,
          commentsCount: 10,
        },
        {
          politician: 'Mayor Ricardo Lim',
          location: 'Public Park, Baguio',
          description: 'Mayor\'s name and photo on park entrance arch. Local government activity.',
          projectType: 'government_activity' as const,
          projectName: 'Public Park Development',
          fundingSource: 'local' as const,
          violationType: 'combination' as const,
          status: 'approved' as const,
          isPublic: true,
          validVotes: 12,
          abuseFlags: 1,
          commentsCount: 7,
        },
        {
          politician: 'Congresswoman Sofia Reyes',
          location: 'Road Infrastructure, Bulacan',
          description: 'Congressional name on road markers. National infrastructure program.',
          projectType: 'government_project' as const,
          projectName: 'Road Infrastructure Development',
          fundingSource: 'national' as const,
          violationType: 'name' as const,
          status: 'pending' as const,
          isPublic: false,
          validVotes: 0,
          abuseFlags: 0,
          commentsCount: 0,
        },
      ];

      // Create canvas images for each report
      const createImage = (text: string, color: string = '#64748b') => {
        const canvas = document.createElement('canvas');
        canvas.width = 600;
        canvas.height = 400;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        // Background
        ctx.fillStyle = '#e2e8f0';
        ctx.fillRect(0, 0, 600, 400);

        // Border
        ctx.strokeStyle = '#cbd5e1';
        ctx.lineWidth = 2;
        ctx.strokeRect(10, 10, 580, 380);

        // Text
        ctx.fillStyle = color;
        ctx.font = 'bold 24px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, 300, 180);

        // Subtitle
        ctx.font = '16px sans-serif';
        ctx.fillStyle = '#94a3b8';
        ctx.fillText('Anti-Epal Watch', 300, 220);

        return canvas.toDataURL('image/jpeg', 0.7);
      };

      let successCount = 0;
      let errorCount = 0;

      for (const data of seedData) {
        try {
          const imageUrl = createImage(data.politician);
          if (!imageUrl) {
            console.warn(`[AdminDashboard] Failed to create image for ${data.politician}`);
            continue;
          }

          await addDoc(
            collection(db, 'artifacts', appId, 'public', 'data', 'reports'),
            {
              politician: data.politician,
              location: data.location,
              description: data.description,
              imageUrl,
              userId: 'system_seed',
              authorName: 'Seed Data Generator',
              createdAt: serverTimestamp(),
              likes: 0,
              status: data.status,
              isPublic: data.isPublic,
              reporter: {
                userId: 'system_seed',
                displayName: 'Seed Data Generator',
                photoUrl: null,
              },
              // DILG Anti-Epal Drive fields
              isGovernmentFunded: true,
              projectType: data.projectType,
              projectName: data.projectName,
              fundingSource: data.fundingSource,
              violationType: data.violationType,
              imageMeta: {
                width: 600,
                height: 400,
                sizeBytes: 0,
                mimeType: 'image/jpeg',
              },
              communitySignals: {
                validVotes: data.validVotes,
                abuseFlags: data.abuseFlags,
                commentsCount: data.commentsCount,
              },
            }
          );
          successCount++;
          console.log(`[AdminDashboard] Successfully added seed report: ${data.politician}`);
        } catch (error) {
          console.error(`[AdminDashboard] Error adding seed report for ${data.politician}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        notify(`Successfully created ${successCount} seed reports! ${errorCount > 0 ? `${errorCount} failed.` : ''}`, 'success');
        console.log(`[AdminDashboard] Successfully created ${successCount} seed reports`);
      } else {
        const errorMsg = errorCount > 0 
          ? `Failed to create seed reports. ${errorCount} errors occurred. Check console for details.`
          : 'Failed to create seed reports. No reports were created.';
        notify(errorMsg, 'error');
        console.error('[AdminDashboard] Failed to create seed reports. All attempts failed.');
      }
    } catch (error) {
      console.error('[AdminDashboard] Error generating seed reports:', error);
      const errorMessage = error instanceof Error 
        ? `Failed to generate seed reports: ${error.message}`
        : 'Failed to generate seed reports. Please check console for details.';
      notify(errorMessage, 'error');
    }
  };

  // Export reports to CSV for DILG
  const exportReportsToCSV = () => {
    const approvedReports = filteredReports.filter((r) => r.status === 'approved' && r.isPublic);
    
    if (approvedReports.length === 0) {
      showNotify?.('No approved public reports to export.', 'info');
      return;
    }

    // CSV headers
    const headers = [
      'Report ID',
      'Politician Name',
      'Location',
      'Project Type',
      'Project Name',
      'Funding Source',
      'Violation Type',
      'Description',
      'Reporter Name',
      'Date Submitted',
      'Valid Votes',
      'Abuse Flags',
      'Comments Count',
    ];

    // CSV rows
    const rows = approvedReports.map((report) => {
      const date = report.createdAt?.toDate 
        ? report.createdAt.toDate().toISOString()
        : new Date().toISOString();
      
      return [
        report.id,
        report.politician || '',
        report.location || '',
        report.projectType?.replace('_', ' ') || '',
        report.projectName || '',
        report.fundingSource || '',
        report.violationType || '',
        (report.description || '').replace(/\n/g, ' ').replace(/,/g, ';'),
        report.authorName || 'Anonymous',
        date,
        report.communitySignals?.validVotes || 0,
        report.communitySignals?.abuseFlags || 0,
        report.communitySignals?.commentsCount || 0,
      ];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `dilg-anti-epal-reports-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showNotify?.(`Exported ${approvedReports.length} reports to CSV`, 'success');
  };

  // Check if we're in modal mode (onClose is provided and we want modal styling)
  const isModalMode = !!onClose;

  const dashboardContent = (
    <div className={`${isModalMode ? 'overflow-y-auto p-6' : ''}`}>
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading dashboard...</div>
            ) : (
              <div className="space-y-6">
                {/* Phase 2: Category Filter */}
                <div>
                  <label htmlFor="admin-category-filter" className="block text-sm font-semibold text-gray-700 mb-2">
                    Filter by Category
                  </label>
                  <select
                    id="admin-category-filter"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value as ReportCategory | 'all')}
                    className="w-full max-w-xs px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Categories</option>
                    <option value="ANTI_EPAL">Anti-Epal Watch</option>
                    <option value="PROJECT_DELAY">Project Delay</option>
                    <option value="GHOST_PROJECT">Ghost Project</option>
                    <option value="SERVICE_FAILURE">Service Failure</option>
                    <option value="COMPLIANCE_GAP">Compliance Gap</option>
                  </select>
                </div>

                {/* Analytics Disclaimer */}
                <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800 italic text-center">
                    Aggregated counts reflect submitted reports, not verified violations or wrongdoing.
                  </p>
                </div>

                {/* Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Total Reports */}
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                    <div className="flex items-center justify-between mb-2">
                      <FileText className="text-blue-600" size={24} />
                      <span className="text-2xl font-bold text-blue-900">
                        {stats.totalReports}
                      </span>
                    </div>
                    <p className="text-sm text-blue-700 font-medium">Total Citizen Reports</p>
                  </div>

                  {/* Pending Reports */}
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100">
                    <div className="flex items-center justify-between mb-2">
                      <Clock className="text-yellow-600" size={24} />
                      <span className="text-2xl font-bold text-yellow-900">
                        {stats.pendingReports}
                      </span>
                    </div>
                    <p className="text-sm text-yellow-700 font-medium">Pending Review</p>
                  </div>

                  {/* Approved Reports */}
                  <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                    <div className="flex items-center justify-between mb-2">
                      <CheckCircle className="text-green-600" size={24} />
                      <span className="text-2xl font-bold text-green-900">
                        {stats.approvedReports}
                      </span>
                    </div>
                    <p className="text-sm text-green-700 font-medium">Approved</p>
                  </div>

                  {/* Public Reports */}
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                    <div className="flex items-center justify-between mb-2">
                      <Eye className="text-purple-600" size={24} />
                      <span className="text-2xl font-bold text-purple-900">
                        {stats.publicReports}
                      </span>
                    </div>
                    <p className="text-sm text-purple-700 font-medium">Publicly Visible</p>
                  </div>
                </div>

                {/* Community Engagement Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="text-green-600" size={20} />
                      <span className="text-lg font-bold text-gray-900">
                        {stats.totalValidVotes}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">Valid Votes</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="text-red-600" size={20} />
                      <span className="text-lg font-bold text-gray-900">
                        {stats.totalAbuseFlags}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">Abuse Flags</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="text-blue-600" size={20} />
                      <span className="text-lg font-bold text-gray-900">
                        {stats.totalComments}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">Total Comments</p>
                  </div>
                </div>

                {/* DILG Analytics */}
                {showNotify && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <BarChart3 size={20} />
                      DILG Analytics
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {/* By Project Type */}
                      <div>
                        <h4 className="font-semibold text-gray-700 text-sm mb-2">By Project Type</h4>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span>Government Project:</span>
                            <span className="font-semibold">{dilgAnalytics.byProjectType.government_project}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Government Program:</span>
                            <span className="font-semibold">{dilgAnalytics.byProjectType.government_program}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Government Activity:</span>
                            <span className="font-semibold">{dilgAnalytics.byProjectType.government_activity}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Government Property:</span>
                            <span className="font-semibold">{dilgAnalytics.byProjectType.government_property}</span>
                          </div>
                        </div>
                      </div>

                      {/* By Funding Source */}
                      <div>
                        <h4 className="font-semibold text-gray-700 text-sm mb-2">By Funding Source</h4>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span>National:</span>
                            <span className="font-semibold">{dilgAnalytics.byFundingSource.national}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Local:</span>
                            <span className="font-semibold">{dilgAnalytics.byFundingSource.local}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Both:</span>
                            <span className="font-semibold">{dilgAnalytics.byFundingSource.both}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* By Violation Type */}
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-700 text-sm mb-2">By Violation Type</h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex justify-between">
                          <span>Name:</span>
                          <span className="font-semibold">{dilgAnalytics.byViolationType.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Image:</span>
                          <span className="font-semibold">{dilgAnalytics.byViolationType.image}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Likeness:</span>
                          <span className="font-semibold">{dilgAnalytics.byViolationType.likeness}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Combination:</span>
                          <span className="font-semibold">{dilgAnalytics.byViolationType.combination}</span>
                        </div>
                      </div>
                    </div>

                    {/* Export Button */}
                    <button
                      onClick={exportReportsToCSV}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
                    >
                      <Download size={18} />
                      Export Reports to CSV (DILG Format)
                    </button>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Exports approved public reports in CSV format for DILG reporting
                    </p>
                  </div>
                )}

                {/* Utilities */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <Database size={20} />
                      Utilities
                  </h3>
                  
                  {/* Generate Seed Reports Button */}
                  <button
                    onClick={generateSeedReports}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Database size={18} />
                    Generate Seed Reports (10 samples)
                  </button>
                  <p className="text-xs text-gray-500 text-center">
                    Creates 10 sample reports with various DILG violation types for testing
                  </p>

                  {/* Generate Single Sample Report Button */}
                    <button
                    onClick={async () => {
                      const handleGenerate = async () => {
                        try {
                          const canvas = document.createElement('canvas');
                          canvas.width = 600;
                          canvas.height = 400;
                          const ctx = canvas.getContext('2d');
                          if (!ctx) return;
                          ctx.fillStyle = '#e2e8f0';
                          ctx.fillRect(0, 0, 600, 400);
                          ctx.fillStyle = '#64748b';
                          ctx.font = 'bold 30px sans-serif';
                          ctx.textAlign = 'center';
                          ctx.fillText('SAMPLE EVIDENCE', 300, 180);

                          await addDoc(
                            collection(db, 'artifacts', appId, 'public', 'data', 'reports'),
                            {
                              politician: 'Mayor Juan Sample',
                              location: 'Public Market, San Pedro',
                              description: 'Huge tarpaulin covering entrance. Verified violation.',
                              imageUrl: canvas.toDataURL('image/jpeg', 0.5),
                              userId: 'system_sample',
                              authorName: 'System Admin',
                              createdAt: serverTimestamp(),
                              likes: 0,
                              status: 'pending',
                              isPublic: false,
                              reporter: {
                                userId: 'system_sample',
                                displayName: 'System Admin',
                                photoUrl: null,
                              },
                              // DILG Anti-Epal Drive fields
                              isGovernmentFunded: true,
                              projectType: 'government_project',
                              projectName: 'Public Market Renovation',
                              fundingSource: 'local',
                              violationType: 'combination',
                              imageMeta: {
                                width: 600,
                                height: 400,
                                sizeBytes: 0,
                                mimeType: 'image/jpeg',
                              },
                              communitySignals: {
                                validVotes: 0,
                                abuseFlags: 0,
                                commentsCount: 0,
                              },
                            }
                          );
                          const notify = showNotify || ((msg: string) => { console.log(msg); alert(msg); });
                          notify('Sample report added!', 'success');
                        } catch (error) {
                          console.error('[AdminDashboard] Error adding sample report:', error);
                          const notify = showNotify || ((msg: string) => { console.error(msg); alert(msg); });
                          notify('Failed to add sample report.', 'error');
                        }
                      };

                      if (confirmAction) {
                        confirmAction('Generate Sample Report?', 'This will add a dummy report for testing.', handleGenerate);
                      } else if (window.confirm('Generate Sample Report?\n\nThis will add a dummy report for testing.')) {
                        await handleGenerate();
                      }
                    }}
                      className="bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-between w-full"
                    >
                      <div className="flex items-center gap-2">
                        <Database size={20} />
                        <span className="font-medium">Generate Sample Report</span>
                      </div>
                      <ArrowRight size={18} />
                    </button>
                  </div>

                {/* Reports Needing Attention */}
                {needsAttention.length > 0 && (
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <h3 className="font-bold text-red-900 mb-3 flex items-center gap-2">
                      <AlertTriangle size={20} />
                      Reports Needing Attention ({needsAttention.length})
                    </h3>
                    <div className="space-y-2">
                      {needsAttention.map((report) => (
                        <div
                          key={report.id}
                          className="bg-white rounded p-3 border border-red-200"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">
                                {report.politician}
                              </p>
                              <p className="text-sm text-gray-600">{report.location}</p>
                              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                <span>
                                  ✓ {report.communitySignals?.validVotes || 0} valid
                                </span>
                                <span>
                                  ✗ {report.communitySignals?.abuseFlags || 0} abuse
                                </span>
                              </div>
                            </div>
                            <span className="text-blue-600 text-sm font-medium">
                              Review in Reports →
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Reports */}
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-gray-900 flex items-center gap-2">
                      <TrendingUp size={20} />
                      Recent Reports
                    </h3>
                    <span className="text-sm text-blue-600 font-medium flex items-center gap-1">
                      View in Reports
                      <ArrowRight size={14} />
                    </span>
                  </div>
                  <div className="space-y-2">
                    {recentReports.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No reports yet
                      </p>
                    ) : (
                      recentReports.map((report) => {
                        const status = report.status || 'pending';
                        return (
                          <div
                            key={report.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-100 hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-12 h-12 rounded overflow-hidden bg-gray-200 shrink-0">
                                {report.imageUrl ? (
                                  <img
                                    src={report.imageUrl}
                                    alt="Report"
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                                    <FileText size={20} />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate">
                                  {report.politician}
                                </p>
                                <p className="text-sm text-gray-600 truncate">
                                  {report.location}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatDate(report.createdAt)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {status === 'pending' && (
                                <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-2 py-1 rounded">
                                  Pending
                                </span>
                              )}
                              {status === 'approved' && (
                                <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded">
                                  Approved
                                </span>
                              )}
                              {status === 'rejected' && (
                                <span className="bg-red-100 text-red-800 text-xs font-semibold px-2 py-1 rounded">
                                  Rejected
                                </span>
                              )}
                              {report.isPublic === false && (
                                <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-1 rounded flex items-center gap-1">
                                  <EyeOff size={12} />
                                  Hidden
                                </span>
                              )}
                              <span className="text-blue-600">
                                <ArrowRight size={18} />
                              </span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Admin Stats (Root Only) */}
                {currentUserRole === 'root' && (
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <Shield size={20} />
                      Admin Statistics
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Total Admins</p>
                        <p className="text-2xl font-bold text-gray-900">{stats.totalAdmins}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Active Admins</p>
                        <p className="text-2xl font-bold text-green-600">
                          {stats.activeAdmins}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
  );


  // If modal mode, wrap in modal container
  if (isModalMode) {
    return (
      <>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full my-8 max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 shrink-0">
              <div className="flex items-center gap-2">
                <Shield className="text-blue-600" size={24} />
                <h2 className="text-2xl font-bold text-gray-900">Admin Dashboard</h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            {dashboardContent}
          </div>
        </div>
      </>
    );
  }

  // Otherwise, return as page component
  return (
    <>
      <div className="w-full">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="text-blue-600" size={28} />
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          </div>
          <p className="text-gray-600">Overview of reports, users, and system statistics</p>
        </div>
        {dashboardContent}
      </div>
    </>
  );
};
